import React from 'react';

import { css, cx } from '@emotion/css';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { Skeleton } from '@mui/material';
import { useForm } from 'react-hook-form';

import { usePatientIdFromURL } from '@/hooks/usePatientIdFromURL.ts';
import { useStyles } from '@/hooks/useStyles.ts';
import { Queries } from '@/queries/Queries.ts';
import { Form } from '@/uiKit/atoms/Form.tsx';
import { MessageAttachments } from '@/uiKit/molecules/chat/MessageAttachments.tsx';
import {
  MessageData,
  messageDataSchema,
} from '@/uiKit/molecules/chat/MessageDataSchema.ts';
import { MessageTextBox } from '@/uiKit/molecules/chat/MessageTextBox.tsx';

export const SEND_MESSAGE_FORM_ID = 'SEND_MESSAGE_FORM';

export type SendMessageFormProps = {
  className?: string;
};

export const SendMessageForm: React.FC<SendMessageFormProps> = ({
  className,
}) => {
  const styles = useStyles(makeStyles);
  const me = Queries.user.usePractitionerMe({});
  const sendMessage = Queries.chat.useCreateMessage();

  const patientId = usePatientIdFromURL();
  const draftMessage = Queries.chat.useDraftMessage({
    patientId,
    practitionerId: me.id,
  });
  const updateDraftMessage = Queries.chat.useUpdateDraftMessage();
  const isBillable = Queries.condition.useCondition(patientId, {
    select: data => data.isBillable,
  });

  const form = useForm<MessageData>({
    defaultValues: {
      // If the patient is not billable, there is no nurse, so the practitioner tab is disabled
      visibility: isBillable ? 'practitioners' : 'all',
      contentText: draftMessage.message ?? '',
      contentAttachment: [],
    },
    resolver: standardSchemaResolver(messageDataSchema),
    criteriaMode: 'all',
    disabled: sendMessage.isPending,
  });

  return (
    <Form
      id={SEND_MESSAGE_FORM_ID}
      className={cx(styles.container, className)}
      onSubmit={({ contentAttachment, ...data }) => {
        const attachments = contentAttachment.map(file => ({
          file,
          name: file.name,
        }));
        // Clear draft
        sendMessage.mutate(
          {
            patientId,
            ...data,
            contentAttachment: attachments.length > 0 ? attachments : undefined,
          },
          {
            onSuccess: () => {
              form.reset({
                visibility: data.visibility,
                contentText: '',
                contentAttachment: [],
              });
              updateDraftMessage.mutate({
                patientId,
                practitionerId: me.id,
                message: undefined,
              });
            },
          },
        );
      }}
      formContext={form}
    >
      <div>
        <MessageAttachments />
        <MessageTextBox />
      </div>
    </Form>
  );
};

export const SendMessageFormSkeleton = () => {
  return <Skeleton width="100%" height="calc(8px + 6rem + 31.75px + 3rem)" />;
};

const makeStyles = () => ({
  container: css({
    width: '100%',
  }),
});
